import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PACOCA_API_URL } from "../../global-components/src/utils/vars";
import { api } from "../../services/api";
import { useAuth } from "../../global-components/src";
import "./contribution-graph.css";

interface Props {
    userName: string | null;
}

type ActivityMap = Record<string, number>;

/** Builds an array of weeks (columns), each with 7 day entries (rows). */
function buildWeeks(data: ActivityMap): { date: string; count: number }[][] {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Go back to the most recent Sunday that is ≥52 full weeks ago
    const start = new Date(today);
    start.setDate(start.getDate() - today.getDay() - 52 * 7);

    const weeks: { date: string; count: number }[][] = [];
    let week: { date: string; count: number }[] = [];

    const cursor = new Date(start);
    while (cursor <= today) {
        const key = cursor.toISOString().slice(0, 10);
        week.push({ date: key, count: data[key] ?? 0 });
        if (week.length === 7) {
            weeks.push(week);
            week = [];
        }
        cursor.setDate(cursor.getDate() + 1);
    }
    // Pad the last partial week with empty cells
    if (week.length > 0) {
        while (week.length < 7) week.push({ date: "", count: -1 });
        weeks.push(week);
    }
    return weeks;
}

function levelFor(count: number, max: number): 0 | 1 | 2 | 3 | 4 {
    if (count <= 0) return 0;
    if (max <= 0) return 1;
    const ratio = count / max;
    if (ratio <= 0.25) return 1;
    if (ratio <= 0.5)  return 2;
    if (ratio <= 0.75) return 3;
    return 4;
}

const MONTH_LABELS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

export default function ContributionGraph({ userName }: Props) {
    const { token } = useAuth();
    const { t, i18n } = useTranslation();
    const [data, setData] = useState<ActivityMap>({});
    const [loading, setLoading] = useState(true);
    const [tooltip, setTooltip] = useState<{ text: string; x: number; y: number } | null>(null);
    const [viewMode, setViewMode] = useState<"monthly" | "annual">("monthly");

    const [currentMonth, setCurrentMonth] = useState(() => new Date().getMonth());
    const [currentYear, setCurrentYear] = useState(() => new Date().getFullYear());

    useEffect(() => {
        if (!userName) return;
        setLoading(true);
        api(token)
            .get(`${PACOCA_API_URL}/users/activity/${userName}`)
            .then((res) => setData(res.data.data ?? {}))
            .catch(() => setData({}))
            .finally(() => setLoading(false));
    }, [userName, token]);

    const weeks = useMemo(() => buildWeeks(data), [data]);
    const max   = useMemo(() => Math.max(0, ...Object.values(data)), [data]);
    const totalPosts = useMemo(() => Object.values(data).reduce((a, b) => a + b, 0), [data]);

    const today = useMemo(() => new Date(), []);
    const isCurrentMonth = useMemo(() => {
        return currentYear === today.getFullYear() && currentMonth === today.getMonth();
    }, [currentYear, currentMonth, today]);

    const monthTotalPosts = useMemo(() => {
        let sum = 0;
        Object.entries(data).forEach(([key, count]) => {
            const parts = key.split("-");
            if (parts.length === 3) {
                const y = parseInt(parts[0], 10);
                const m = parseInt(parts[1], 10) - 1; // 0-indexed
                if (y === currentYear && m === currentMonth) {
                    sum += count;
                }
            }
        });
        return sum;
    }, [data, currentMonth, currentYear]);

    const handlePrevMonth = () => {
        setCurrentMonth((prev) => {
            if (prev === 0) {
                setCurrentYear((y) => y - 1);
                return 11;
            }
            return prev - 1;
        });
    };

    const handleNextMonth = () => {
        if (isCurrentMonth) return;
        setCurrentMonth((prev) => {
            if (prev === 11) {
                setCurrentYear((y) => y + 1);
                return 0;
            }
            return prev + 1;
        });
    };

    const monthYearLabel = useMemo(() => {
        const localeMap: Record<string, string> = {
            pt: "pt-BR",
            pt_PT: "pt-PT",
            pt_BR: "pt-BR",
            es: "es-ES",
            us: "en-US",
            en: "en-US",
            de: "de-DE",
            fr: "fr-FR",
            it: "it-IT",
            ja: "ja-JP",
            zh: "zh-CN",
            ar: "ar-SA"
        };
        const locale = localeMap[i18n.language] || "pt-BR";
        const dateObj = new Date(currentYear, currentMonth, 1);
        const label = dateObj.toLocaleDateString(locale, {
            month: "long",
            year: "numeric"
        });
        return label.charAt(0).toUpperCase() + label.slice(1);
    }, [currentMonth, currentYear, i18n.language]);

    const monthGrid = useMemo(() => {
        const firstDayOfWeek = new Date(currentYear, currentMonth, 1).getDay(); // 0 = Sun
        const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

        const grid: { date: string; dayNumber: number; count: number }[] = [];

        // Padding before the 1st
        for (let i = 0; i < firstDayOfWeek; i++) {
            grid.push({ date: "", dayNumber: 0, count: -1 });
        }

        // Month days
        for (let day = 1; day <= daysInMonth; day++) {
            const mm = String(currentMonth + 1).padStart(2, "0");
            const dd = String(day).padStart(2, "0");
            const key = `${currentYear}-${mm}-${dd}`;
            grid.push({ date: key, dayNumber: day, count: data[key] ?? 0 });
        }

        // Padding to complete last week
        while (grid.length % 7 !== 0) {
            grid.push({ date: "", dayNumber: 0, count: -1 });
        }

        return grid;
    }, [currentYear, currentMonth, data]);

    // One label per column: show month name only when the month changes
    const monthRow = useMemo(() => {
        let lastMonth = -1;
        return weeks.map((week) => {
            const first = week.find((d) => d.date);
            if (!first) return "";
            const m = new Date(first.date + "T12:00:00").getMonth();
            if (m !== lastMonth) { lastMonth = m; return MONTH_LABELS[m]; }
            return "";
        });
    }, [weeks]);

    const handleMouseEnter = (e: React.MouseEvent, date: string, count: number) => {
        if (!date) return;
        const rect = (e.target as HTMLElement).getBoundingClientRect();
        const d = new Date(date + "T12:00:00");
        const formatted = d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
        const text = count > 0
            ? t("account.contributionGraph.tooltipPosts", { count, date: formatted })
            : t("account.contributionGraph.tooltipNone", { date: formatted });
        setTooltip({ text, x: rect.left + rect.width / 2, y: rect.top - 8 });
    };

    if (loading) {
        return (
            <div className="cg-wrapper cg-skeleton">
                <div className="cg-placeholder" />
            </div>
        );
    }

    return (
        <div className="cg-wrapper">
            <div className="cg-header">
                <div className="cg-header-left">
                    <span className="cg-title">{t("account.contributionGraph.title")}</span>
                    <span className="cg-total">
                        {viewMode === "monthly"
                            ? t("account.contributionGraph.total_month", { count: monthTotalPosts })
                            : t("account.contributionGraph.total", { count: totalPosts })}
                    </span>
                </div>
                <div className="cg-toggle-container">
                    <button
                        className={`cg-toggle-btn ${viewMode === "monthly" ? "active" : ""}`}
                        onClick={() => setViewMode("monthly")}
                    >
                        {t("account.contributionGraph.monthly")}
                    </button>
                    <button
                        className={`cg-toggle-btn ${viewMode === "annual" ? "active" : ""}`}
                        onClick={() => setViewMode("annual")}
                    >
                        {t("account.contributionGraph.annual")}
                    </button>
                </div>
            </div>

            {viewMode === "monthly" ? (
                <div className="cg-month-container">
                    <div className="cg-month-nav">
                        <button className="cg-nav-btn" onClick={handlePrevMonth}>
                            &lt;
                        </button>
                        <span className="cg-month-label">{monthYearLabel}</span>
                        <button
                            className="cg-nav-btn"
                            onClick={handleNextMonth}
                            disabled={isCurrentMonth}
                        >
                            &gt;
                        </button>
                    </div>

                    <div className="cg-month-grid-wrapper">
                        <div className="cg-month-weekdays">
                            <span className="cg-month-weekday-cell">{t("account.contributionGraph.day_sun")}</span>
                            <span className="cg-month-weekday-cell">{t("account.contributionGraph.day_mon")}</span>
                            <span className="cg-month-weekday-cell">{t("account.contributionGraph.day_tue")}</span>
                            <span className="cg-month-weekday-cell">{t("account.contributionGraph.day_wed")}</span>
                            <span className="cg-month-weekday-cell">{t("account.contributionGraph.day_thu")}</span>
                            <span className="cg-month-weekday-cell">{t("account.contributionGraph.day_fri")}</span>
                            <span className="cg-month-weekday-cell">{t("account.contributionGraph.day_sat")}</span>
                        </div>

                        <div className="cg-month-grid">
                            {monthGrid.map((day, idx) => (
                                <div
                                    key={idx}
                                    className={`cg-month-cell-item cg-level-${day.date ? levelFor(day.count, max) : "empty"}`}
                                    onMouseEnter={(e) => day.date && handleMouseEnter(e, day.date, day.count)}
                                    onMouseLeave={() => setTooltip(null)}
                                >
                                    {day.dayNumber > 0 && (
                                        <span className="cg-month-cell-number">{day.dayNumber}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="cg-scroll">
                    <div className="cg-inner">
                        {/* Month labels — one cell per week column */}
                        <div className="cg-months-row">
                            {monthRow.map((label, col) => (
                                <span key={col} className="cg-month-cell">{label}</span>
                            ))}
                        </div>

                        {/* Grid — one .cg-week per column */}
                        <div className="cg-grid">
                            {weeks.map((week, col) => (
                                <div key={col} className="cg-week">
                                    {week.map((day, row) => (
                                        <div
                                            key={row}
                                            className={`cg-cell cg-level-${day.date ? levelFor(day.count, max) : "empty"}`}
                                            onMouseEnter={(e) => handleMouseEnter(e, day.date, day.count)}
                                            onMouseLeave={() => setTooltip(null)}
                                        />
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {tooltip && (
                <div
                    className="cg-tooltip"
                    style={{ left: tooltip.x, top: tooltip.y }}
                >
                    {tooltip.text}
                </div>
            )}
        </div>
    );
}
